import React, { useState, useEffect } from 'react';
import {
  ShoppingCart, FileText, Package, ListChecks, BarChart3, History,
  Truck, ClipboardList, TrendingUp, MapPin, Wrench, Database, Calculator,
  ChevronDown, ChevronRight, Grid3x3, Flame, Radio, Activity, Pin, PinOff, Settings,
  Zap, Edit, Move, ScrollText, FolderTree, Link2, Box, Users, Clock, Stethoscope, Tag, ScanLine, Shield,
  FilePlus, ShoppingBag, MessageSquare, Bug, Send, ArrowRightLeft, Layers, Edit3, Palette
} from 'lucide-react';
import { Card } from '../../common/Card';
import { useAuth } from '../../../contexts/AuthContext';
import { useModuleAccess } from '../../../hooks/useModuleAccess';
import { useWarehouse } from '../../../contexts/WarehouseContext';
import { useTranslation } from '../../../contexts/I18nProvider';
import { OPERATOR_MODULES } from '../../../constants/operatorModules';
import shortcutService from '../../../services/shortcutService';
import { usePublishedDate } from '../../../hooks/usePublishedDate';

interface ManagerDashboardProps {
  onModuleChange: (moduleId: string, event?: React.MouseEvent) => void;
}

// Interface for gruppering/undergruppering struktur
interface DashboardItem {
  id: string;
  name: string;
  icon: any;
  iconName: string; // String name of the icon for serialization
  color: string;
  description: string;
  moduleId?: string; // Hvis det finnes en faktisk modul å navigere til
}

interface DashboardSection {
  id: string;
  name: string;
  items: DashboardItem[];
}

interface DashboardGroup {
  id: string;
  name: string;
  icon: any;
  color: string;
  sections: DashboardSection[];
}

export const ManagerDashboard: React.FC<ManagerDashboardProps> = ({ onModuleChange }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { currentWarehouse, warehouses } = useWarehouse();
  const isAdvancedWarehouse = warehouses.find(w => w.warehouseID === currentWarehouse)?.useAWYesNo ?? false;
  const [pinnedShortcuts, setPinnedShortcuts] = useState<Set<string>>(new Set());

  // Load pinned shortcuts on mount
  useEffect(() => {
    const shortcuts = shortcutService.getShortcuts();
    setPinnedShortcuts(new Set(shortcuts.map(s => s.id)));
  }, []);

  // Listen for shortcut changes from other components
  useEffect(() => {
    const handleShortcutsChanged = () => {
      const shortcuts = shortcutService.getShortcuts();
      setPinnedShortcuts(new Set(shortcuts.map(s => s.id)));
    };

    window.addEventListener('shortcuts-changed', handleShortcutsChanged);
    return () => window.removeEventListener('shortcuts-changed', handleShortcutsChanged);
  }, []);

  // Hent siste publiseringsdato fra version.json (generert ved build)
  const publishedDate = usePublishedDate();

  // Access control based on operatorGroupId
  // GroupID 1 (Operatør): Only operator functions
  // GroupID 2 (Lagersjef): All functions
  // GroupID 3 (IPB/Admin): All functions
  const { isModuleAccessible } = useModuleAccess();
  const isOperatorOnly = user?.operatorGroupId === '1';
  const canAccessManagerFunctions = user?.operatorGroupId === '2' || user?.operatorGroupId === '3';
  const isSystemAdmin = user?.operatorGroupId === '3';

  // Definer dashboardstrukturen
  const dashboardGroups: DashboardGroup[] = [
    {
      id: 'plukk-pakk',
      name: 'Plukk & Pakk',
      icon: Package,
      color: 'blue',
      sections: [
        {
          id: 'nye-ordre',
          name: 'Nye Ordre',
          items: [
            {
              id: 'ordre',
              name: 'Ordre',
              icon: ShoppingCart,
              iconName: 'ShoppingCart',
              color: 'blue',
              description: 'Administrer nye ordre',
              moduleId: 'new-orders' // Navigerer til NewOrders
            },
            {
              id: 'ordrelinje',
              name: 'Ordrelinje',
              icon: FileText,
              iconName: 'FileText',
              color: 'blue',
              description: 'Detaljert ordrelinjevisning',
              moduleId: 'order-lines' // Navigerer til OrderLines
            }
          ]
        },
        {
          id: 'ordre-under-arbeid',
          name: 'Plukklister',
          items: [
            {
              id: 'nye-plukklister',
              name: 'Nye plukklister',
              icon: ListChecks,
              iconName: 'ListChecks',
              color: 'green',
              description: 'Se nye plukklister',
              moduleId: 'new-picking-lists'
            },
            {
              id: 'plukklister-arbeid',
              name: 'Plukklister under arbeid',
              icon: Package,
              iconName: 'Package',
              color: 'yellow',
              description: 'Aktive plukklister',
              moduleId: 'working-picking-lists'
            }
          ]
        },
        {
          id: 'historikk-plukk',
          name: 'Historikk',
          items: [
            {
              id: 'avsluttede-plukklister',
              name: 'Avsluttede plukklister',
              icon: ListChecks,
              iconName: 'ListChecks',
              color: 'gray',
              description: 'Se avsluttede plukklister',
              moduleId: 'completed-picking-lists'
            },
            {
              id: 'ordrehistorikk',
              name: 'Ordrehistorikk',
              icon: History,
              iconName: 'History',
              color: 'gray',
              description: 'Historiske ordre',
              moduleId: 'order-history'
            },
            {
              id: 'statistikk',
              name: 'Statistikk',
              icon: BarChart3,
              iconName: 'BarChart3',
              color: 'purple',
              description: 'Statistikk og rapporter',
              moduleId: 'statistics'
            }
          ]
        }
      ]
    },
    {
      id: 'varemottak',
      name: 'Varemottak',
      icon: Truck,
      color: 'purple',
      sections: [
        {
          id: 'bestilling',
          name: 'Bestilling',
          items: [
            {
              id: 'bestilling',
              name: 'Administrer bestillinger',
              icon: FileText,
              iconName: 'FileText',
              color: 'purple',
              description: 'Administrer bestillinger',
              moduleId: 'working-orders'
            },
            {
              id: 'bestillingslinje',
              name: 'Bestillingslinje',
              icon: FileText,
              iconName: 'FileText',
              color: 'purple',
              description: 'Detaljert bestillingslinjevisning',
              moduleId: 'purchase-order-lines'
            }
          ]
        },
        {
          id: 'historikk-varemottak',
          name: 'Historikk',
          items: [
            {
              id: 'historikk-bestilling',
              name: 'Bestillingshistorikk',
              icon: History,
              iconName: 'History',
              color: 'gray',
              description: 'Bestillingshistorikk',
              moduleId: 'purchase-order-history'
            }
          ]
        }
      ]
    },
    {
      id: 'lager',
      name: 'Lager',
      icon: Database,
      color: 'teal',
      sections: [
        {
          id: 'transaksjoner',
          name: 'Transaksjoner',
          items: [
            {
              id: 'vare-lokasjonssaldo',
              name: 'Vare og lokasjonssaldo',
              icon: TrendingUp,
              iconName: 'TrendingUp',
              color: 'teal',
              description: 'Saldo oversikt',
              moduleId: 'item-location-balance'
            },
            {
              id: 'transaksjonshistorikk',
              name: 'Transaksjonshistorikk',
              icon: History,
              iconName: 'History',
              color: 'teal',
              description: 'Historiske transaksjoner',
              moduleId: 'transaction-history'
            },
            {
              id: 'reservasjoner',
              name: 'Reservasjoner',
              icon: ClipboardList,
              iconName: 'ClipboardList',
              color: 'teal',
              description: 'Vareserverasjoner',
              moduleId: 'reservations'
            }
          ]
        },
        {
          id: 'vareplassering',
          name: 'Vareplassering',
          items: [
            {
              id: 'grunnlag',
              name: 'Grunnlag',
              icon: Database,
              iconName: 'Database',
              color: 'green',
              description: 'Grunnlag for vareplassering',
              moduleId: 'bin-placement-grunnlag'
            },
            {
              id: 'plassering-ordre',
              name: 'Ordre',
              icon: ShoppingCart,
              iconName: 'ShoppingCart',
              color: 'green',
              description: 'Plasseringsordre',
              moduleId: 'put-away'
            },
            {
              id: 'plassering-ordrelinjer',
              name: 'Ordrelinjer',
              icon: FileText,
              iconName: 'FileText',
              color: 'green',
              description: 'Plasseringsordrelinjer',
              moduleId: 'put-away'
            }
          ]
        },
        {
          id: 'verktoy',
          name: 'Verktøy',
          items: [
            {
              id: 'oprett-utlegg',
              name: 'Oprett utlegg',
              icon: Wrench,
              iconName: 'Wrench',
              color: 'orange',
              description: 'Oprett utlegg',
              moduleId: 'movement'
            },
            {
              id: 'oprett-pafylling',
              name: 'Opprett påfylling',
              icon: Wrench,
              iconName: 'Wrench',
              color: 'orange',
              description: 'Opprett påfylling',
              moduleId: 'movement'
            }
          ]
        },
        {
          id: 'grunndata',
          name: 'Grunndata',
          items: [
            {
              id: 'vedlikehold-lager',
              name: 'Vedlikehold Lager',
              icon: Wrench,
              iconName: 'Wrench',
              color: 'indigo',
              description: 'Vedlikehold av lager',
              moduleId: 'warehouse-maintenance'
            },
            {
              id: 'reolbygger',
              name: 'Reolbygger',
              icon: Grid3x3,
              iconName: 'Grid3x3',
              color: 'indigo',
              description: 'Administrer reoler',
              moduleId: 'rack-builder'
            },
            {
              id: 'location-heatmap',
              name: 'Lokasjon HeatMap',
              icon: Flame,
              iconName: 'Flame',
              color: 'indigo',
              description: 'Visualiser lokasjonsaktivitet',
              moduleId: 'location-heatmap'
            },
            {
              id: 'rack-builder-live',
              name: 'Live Aktivitet',
              icon: Radio,
              iconName: 'Radio',
              color: 'indigo',
              description: 'Sanntidsvisning av lageraktivitet',
              moduleId: 'rack-builder-live'
            },
            {
              id: 'realtime-stats',
              name: 'Sanntidsstatistikk',
              icon: Activity,
              iconName: 'Activity',
              color: 'indigo',
              description: 'Lett visning med statistikk og aktivitetsstrøm',
              moduleId: 'realtime-stats'
            },
            {
              id: 'warehouse-layout-3d',
              name: '3D Lagervisning',
              icon: Box,
              iconName: 'Box',
              color: 'indigo',
              description: 'Visuell 3D-representasjon av lageret',
              moduleId: 'warehouse-layout-3d'
            },
          ]
        },
        {
          id: 'lagertelling',
          name: 'Lagertelling',
          items: [
            {
              id: 'telledashboard',
              name: 'Telledashboard',
              icon: Calculator,
              iconName: 'Calculator',
              color: 'red',
              description: 'Oversikt over tellelister og fritelling',
              moduleId: 'inventory-counting-dashboard'
            },
            {
              id: 'counting-list-creation',
              name: 'Opprett tellelister',
              icon: ClipboardList,
              iconName: 'ClipboardList',
              color: 'orange',
              description: 'Opprett nye tellelister med varer og lokasjoner',
              moduleId: 'counting-list-creation'
            },
            {
              id: 'erp-balance-comparison',
              name: 'ERP-Saldo Sammenligning',
              icon: ArrowRightLeft,
              iconName: 'ArrowRightLeft',
              color: 'purple',
              description: 'Sammenlign WMS-saldo mot ERP-saldo, opprett tellelister fra avvik',
              moduleId: 'inventory-counting-dashboard-balance-comparison'
            },
          ]
        }
      ]
    },
    {
      id: 'system',
      name: 'System',
      icon: Settings,
      color: 'gray',
      sections: [
        {
          id: 'konfigurajon',
          name: 'Konfigurasjon',
          items: [
            {
              id: 'order-timeline',
              name: 'Ordretidslinje',
              icon: Clock,
              iconName: 'Clock',
              color: 'blue',
              description: 'Hendelsesforløp og feilsøking for ordre',
              moduleId: 'order-timeline'
            },
            {
              id: 'lagersted',
              name: 'Lagersted',
              icon: Database,
              iconName: 'Database',
              color: 'gray',
              description: 'Administrer lagersted konfigurasjoner',
              moduleId: 'warehouse-management'
            },
            {
              id: 'regler',
              name: 'Regler',
              icon: ScrollText,
              iconName: 'ScrollText',
              color: 'gray',
              description: 'Vedlikehold systemregler og SQL-uttrykk',
              moduleId: 'system-rules'
            },
            {
              id: 'regelgrupper',
              name: 'Regelgrupper',
              icon: FolderTree,
              iconName: 'FolderTree',
              color: 'gray',
              description: 'Administrer grupper for systemregler',
              moduleId: 'system-rule-groups'
            },
            {
              id: 'regelsammensetning',
              name: 'Regelsammensetning',
              icon: Link2,
              iconName: 'Link2',
              color: 'gray',
              description: 'Koble regler til regelgrupper med drag-and-drop',
              moduleId: 'rule-composition'
            },
            {
              id: 'sql-syntax',
              name: 'SQL Syntaks',
              icon: Database,
              iconName: 'Database',
              color: 'gray',
              description: 'Administrer SQL filter, gruppering og sortering',
              moduleId: 'sql-syntax-admin'
            },
            {
              id: 'profile-admin',
              name: 'Profiler',
              icon: Users,
              iconName: 'Users',
              color: 'gray',
              description: 'Administrer brukerprofiler og tilganger',
              moduleId: 'profile-admin'
            },
            {
              id: 'system-settings',
              name: 'Systeminnstillinger',
              icon: Settings,
              iconName: 'Settings',
              color: 'gray',
              description: 'Globale systemkonfigurasjoner',
              moduleId: 'system-settings'
            },
            {
              id: 'reason-codes',
              name: 'Årsakskoder',
              icon: Tag,
              iconName: 'Tag',
              color: 'gray',
              description: 'Vedlikehold årsakskoder for transaksjoner',
              moduleId: 'reason-codes'
            },
            {
              id: 'delivery-methods',
              name: 'Leveringsmåter',
              icon: Truck,
              iconName: 'Truck',
              color: 'gray',
              description: 'Vedlikehold leveringsmåter og fraktkonfigurasjon',
              moduleId: 'delivery-methods'
            },
            {
              id: 'ta-consignor-mappings',
              name: 'Avsendere (TA)',
              icon: ArrowRightLeft,
              iconName: 'ArrowRightLeft',
              color: 'gray',
              description: 'Mapping av ERP-avsenderkoder til TA-systemer',
              moduleId: 'ta-consignor-mappings'
            },
            {
              id: 'pack-zones',
              name: 'Pakkesoner',
              icon: Package,
              iconName: 'Package',
              color: 'gray',
              description: 'Administrer pakkesoner og skriverinnstillinger',
              moduleId: 'pack-zones'
            },
            {
              id: 'filter-groups',
              name: 'Filtergrupper',
              icon: Layers,
              iconName: 'Layers',
              color: 'gray',
              description: 'Administrer filtergrupper og filterkonfigurasjoner',
              moduleId: 'filter-groups'
            },
            {
              id: 'administration',
              name: 'Administrasjon',
              icon: Wrench,
              iconName: 'Wrench',
              color: 'gray',
              description: 'Systemadministrasjon og diagnostikk',
              moduleId: 'administration'
            }
          ]
        }
      ]
    },
    // Systemadministrator — kun synlig for operatorGroupId === '3'
    ...(isSystemAdmin ? [{
      id: 'sysadmin',
      name: 'Systemadministrator',
      icon: Shield,
      color: 'red',
      sections: [
        {
          id: 'sysadmin-tools',
          name: 'Verktøy',
          items: [
            {
              id: 'admin-data-inspector',
              name: 'Data-inspeksjon',
              icon: Database,
              iconName: 'Database',
              color: 'red',
              description: 'Inspiser alle datatables med paginering',
              moduleId: 'admin-data-inspector'
            },
            {
              id: 'admin-order-registration',
              name: 'Ordreregistrering',
              icon: FilePlus,
              iconName: 'FilePlus',
              color: 'red',
              description: 'Registrer nye salgsordre manuelt',
              moduleId: 'order-registration'
            },
            {
              id: 'admin-porder-registration',
              name: 'Innkjøpsordreregistrering',
              icon: ShoppingBag,
              iconName: 'ShoppingBag',
              color: 'red',
              description: 'Registrer nye innkjøpsordre manuelt',
              moduleId: 'porder-registration'
            },
            {
              id: 'admin-claude-query',
              name: 'Spør Compass',
              icon: MessageSquare,
              iconName: 'MessageSquare',
              color: 'red',
              description: 'AI-assistert spørring og analyse',
              moduleId: 'claude-query'
            },
            {
              id: 'admin-test',
              name: 'Test',
              icon: Bug,
              iconName: 'Bug',
              color: 'red',
              description: 'Test og debugging av systemfunksjonalitet',
              moduleId: 'test'
            },
            {
              id: 'admin-document-delivery',
              name: 'Dokumentlevering',
              icon: Send,
              iconName: 'Send',
              color: 'red',
              description: 'eSENTRAL dokumentmottak — innstillinger og historikk',
              moduleId: 'document-delivery'
            },
            {
              id: 'admin-item-type-change',
              name: 'Bytt varetype',
              icon: ArrowRightLeft,
              iconName: 'ArrowRightLeft',
              color: 'red',
              description: 'Endre ItemType med kontrollert migrering av ItemBins',
              moduleId: 'item-type-change'
            },
            {
              id: 'admin-placeholder-editor',
              name: 'Rediger serie-/batchnummer',
              icon: Edit3,
              iconName: 'Edit3',
              color: 'red',
              description: 'Masseredigering av serie- og batchnummer per lokasjon',
              moduleId: 'placeholder-editor'
            },
            {
              id: 'admin-bestillingssystem',
              name: 'Bestillingssystem',
              icon: ShoppingCart,
              iconName: 'ShoppingCart',
              color: 'red',
              description: 'Vedlikehold av selskap, avdelinger, bestillingsvarer og saldo',
              moduleId: 'bestillingssystem-admin'
            },
            {
              id: 'admin-rapport-designer',
              name: 'Rapport-designer',
              icon: Palette,
              iconName: 'Palette',
              color: 'red',
              description: 'Design egendefinerte etiketter og rapporter',
              moduleId: 'rapport-designer'
            },
            {
              id: 'admin-rapportkonfigurasjon',
              name: 'Rapportkonfigurasjon',
              icon: FileText,
              iconName: 'FileText',
              color: 'red',
              description: 'Administrer rapporter og utskrifter',
              moduleId: 'report-configuration'
            }
          ]
        }
      ]
    }] : [])
  ];

  // Operator modules — autoritativ liste delt med Dashboard og SideMenu
  const operatorModules: DashboardItem[] = OPERATOR_MODULES
    .filter(m => !m.requiresAdvancedWarehouse || isAdvancedWarehouse)
    .map(m => ({
      id: m.id,
      name: t(m.nameKey, {}, m.nameFallback),
      icon: m.icon,
      iconName: m.iconName,
      color: m.color,
      description: t(m.descriptionKey, {}, m.descriptionFallback),
      moduleId: m.moduleId
    }));

  const handleItemClick = (item: DashboardItem, event?: React.MouseEvent) => {
    if (item.moduleId) {
      onModuleChange(item.moduleId, event);
    }
    // Hvis ingen moduleId, gjør ingenting (dummy link)
  };

  const handleTogglePin = (item: DashboardItem, groupId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click

    if (!item.moduleId) return; // Can't pin items without moduleId

    const shortcutGroup = mapGroupIdToShortcutGroup(groupId);

    shortcutService.toggleShortcut({
      id: item.id,
      title: item.name,
      path: item.moduleId,
      icon: item.iconName,
      group: shortcutGroup
    });
  };

  const mapGroupIdToShortcutGroup = (groupId: string): any => {
    const groupMap: Record<string, string> = {
      'plukk-pakk': 'plukk-pakk',
      'varemottak': 'varemottak',
      'lager': 'lager',
      'historikk': 'historikk',
      'vareplassering': 'vareplassering',
      'transaksjoner': 'transaksjoner',
      'verktoy': 'verktoy',
      'grunndata': 'grunndata',
      'lagerstilling': 'lagerstilling',
      'system': 'system',
      'sysadmin': 'sysadmin'
    };
    return groupMap[groupId] || 'lager';
  };

  const getColorClasses = (color: string) => {
    const colorMap: { [key: string]: string } = {
      blue: 'bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-900',
      purple: 'bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-900',
      green: 'bg-green-50 hover:bg-green-100 border-green-200 text-green-900',
      yellow: 'bg-yellow-50 hover:bg-yellow-100 border-yellow-200 text-yellow-900',
      teal: 'bg-teal-50 hover:bg-teal-100 border-teal-200 text-teal-900',
      cyan: 'bg-cyan-50 hover:bg-cyan-100 border-cyan-200 text-cyan-900',
      gray: 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-900',
      red: 'bg-red-50 hover:bg-red-100 border-red-200 text-red-900',
      orange: 'bg-orange-50 hover:bg-orange-100 border-orange-200 text-orange-900',
      indigo: 'bg-indigo-50 hover:bg-indigo-100 border-indigo-200 text-indigo-900'
    };
    return colorMap[color] || 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-900';
  };

  const getIconColorClasses = (color: string) => {
    const iconColorMap: { [key: string]: string } = {
      blue: 'text-blue-600',
      purple: 'text-purple-600',
      green: 'text-green-600',
      yellow: 'text-yellow-600',
      teal: 'text-teal-600',
      cyan: 'text-cyan-600',
      gray: 'text-gray-600',
      red: 'text-red-600',
      orange: 'text-orange-600',
      indigo: 'text-indigo-600'
    };
    return iconColorMap[color] || 'text-gray-600';
  };

  const getGroupColorClasses = (color: string) => {
    const colorMap: { [key: string]: string } = {
      blue: 'bg-blue-100 border-blue-300 text-blue-900',
      purple: 'bg-purple-100 border-purple-300 text-purple-900',
      teal: 'bg-teal-100 border-teal-300 text-teal-900',
      red: 'bg-red-100 border-red-300 text-red-900'
    };
    return colorMap[color] || 'bg-gray-100 border-gray-300 text-gray-900';
  };

  return (
    <div className="relative min-h-screen bg-gray-50">
      {/* Main content area - med padding bottom for fast statusbar */}
      <div className="pb-20">
        <div className="p-4 w-full">
          {/* Header område */}
          <div className="mb-6">
            <div className="bg-gradient-to-r from-teal-500 to-blue-600 text-white p-6 rounded-lg shadow-lg flex items-center gap-6">
              <img src="/compass-logo-white.png" alt="Compass" className="h-12 object-contain flex-shrink-0" />
              <div className="w-px h-12 bg-white/40 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold mb-2">Velkommen, {user?.operatorName}!</h2>
                <div className="text-sm opacity-90">
                  <p>
                    {isOperatorOnly
                      ? 'Operatør Dashboard • Utfør dine daglige oppgaver'
                      : 'Lagersjef Dashboard • Administrer alle lageroperasjoner'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Manager functions - Only visible for Lagersjef and Admin */}
          {canAccessManagerFunctions && (
            <div className="space-y-6 mb-6">
              {/* ROW 1: Plukk & Pakk og Varemottak side ved side - samme stil som Lager */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {dashboardGroups.filter(g => g.id === 'plukk-pakk' || g.id === 'varemottak').map((group) => {
                  const GroupIcon = group.icon;
                  return (
                    <div key={group.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
                      <div className={`p-4 rounded-t-lg ${getGroupColorClasses(group.color)} border-b-2`}>
                        <div className="flex items-center space-x-3">
                          <GroupIcon size={24} className={getIconColorClasses(group.color)} />
                          <h3 className="font-bold text-lg">{group.name}</h3>
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="grid grid-cols-2 xl:grid-cols-3 gap-6">
                          {group.sections.map((section) => (
                            <div key={section.id}>
                              <h4 className="font-semibold text-sm text-gray-600 mb-3 uppercase tracking-wide border-b border-gray-100 pb-2">
                                {section.name}
                              </h4>
                              <div className="space-y-2">
                                {section.items.map((item) => {
                                  const ItemIcon = item.icon;
                                  const isPinned = pinnedShortcuts.has(item.id);
                                  return (
                                    <div key={item.id} className="relative group">
                                      <button
                                        onClick={(e) => handleItemClick(item, e)}
                                        className={`${getColorClasses(item.color)} w-full p-3 rounded-lg border-2 transition-all duration-200 transform hover:scale-105 hover:shadow-lg text-left flex items-center gap-3 ${
                                          !item.moduleId ? 'cursor-default opacity-75' : 'cursor-pointer'
                                        }`}
                                        disabled={!item.moduleId}
                                      >
                                        <ItemIcon size={20} className={`${getIconColorClasses(item.color)} flex-shrink-0`} />
                                        <div className="min-w-0">
                                          <h5 className="font-semibold text-xs leading-tight truncate">{item.name}</h5>
                                          <p className="text-[10px] opacity-70 truncate">{item.description}</p>
                                        </div>
                                      </button>
                                      {item.moduleId && (
                                        <button
                                          onClick={(e) => handleTogglePin(item, group.id, e)}
                                          className={`absolute top-1.5 right-1.5 p-1 rounded transition-all duration-200 ${
                                            isPinned ? 'bg-blue-500 text-white opacity-100' : 'bg-white/80 text-gray-600 opacity-0 group-hover:opacity-100'
                                          } hover:scale-110 shadow-sm`}
                                          title={isPinned ? 'Fjern fra snarveier' : 'Legg til snarvei'}
                                        >
                                          {isPinned ? <Pin size={12} /> : <PinOff size={12} />}
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* ROW 2: Lager - full bredde */}
              {dashboardGroups.filter(g => g.id === 'lager').map((group) => {
                const GroupIcon = group.icon;
                return (
                  <div key={group.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className={`p-4 rounded-t-lg ${getGroupColorClasses(group.color)} border-b-2`}>
                      <div className="flex items-center space-x-3">
                        <GroupIcon size={24} className={getIconColorClasses(group.color)} />
                        <h3 className="font-bold text-lg">{group.name}</h3>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                        {group.sections.map((section) => (
                          <div key={section.id}>
                            <h4 className="font-semibold text-sm text-gray-600 mb-3 uppercase tracking-wide border-b border-gray-100 pb-2">
                              {section.name}
                            </h4>
                            <div className="space-y-2">
                              {section.items.map((item) => {
                                const ItemIcon = item.icon;
                                const isPinned = pinnedShortcuts.has(item.id);
                                return (
                                  <div key={item.id} className="relative group">
                                    <button
                                      onClick={(e) => handleItemClick(item, e)}
                                      className={`${getColorClasses(item.color)} w-full p-3 rounded-lg border-2 transition-all duration-200 transform hover:scale-105 hover:shadow-lg text-left flex items-center gap-3 ${
                                        !item.moduleId ? 'cursor-default opacity-75' : 'cursor-pointer'
                                      }`}
                                      disabled={!item.moduleId}
                                    >
                                      <ItemIcon size={20} className={`${getIconColorClasses(item.color)} flex-shrink-0`} />
                                      <div className="min-w-0">
                                        <h5 className="font-semibold text-xs leading-tight truncate">{item.name}</h5>
                                        <p className="text-[10px] opacity-70 truncate">{item.description}</p>
                                      </div>
                                    </button>
                                    {item.moduleId && (
                                      <button
                                        onClick={(e) => handleTogglePin(item, group.id, e)}
                                        className={`absolute top-1.5 right-1.5 p-1 rounded transition-all duration-200 ${
                                          isPinned ? 'bg-blue-500 text-white opacity-100' : 'bg-white/80 text-gray-600 opacity-0 group-hover:opacity-100'
                                        } hover:scale-110 shadow-sm`}
                                        title={isPinned ? 'Fjern fra snarveier' : 'Legg til snarvei'}
                                      >
                                        {isPinned ? <Pin size={12} /> : <PinOff size={12} />}
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* ROW 3: System - full bredde */}
              {dashboardGroups.filter(g => g.id === 'system').map((group) => {
                const GroupIcon = group.icon;
                return (
                  <div key={group.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className={`p-4 rounded-t-lg bg-gray-100 border-gray-300 border-b-2`}>
                      <div className="flex items-center space-x-3">
                        <GroupIcon size={24} className="text-gray-600" />
                        <h3 className="font-bold text-lg text-gray-900">{group.name}</h3>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
                        {group.sections.flatMap((section) =>
                          section.items.map((item) => {
                            const ItemIcon = item.icon;
                            const isPinned = pinnedShortcuts.has(item.id);
                            return (
                              <div key={item.id} className="relative group">
                                <button
                                  onClick={(e) => handleItemClick(item, e)}
                                  className={`${getColorClasses(item.color)} w-full p-3 rounded-lg border-2 transition-all duration-200 transform hover:scale-105 hover:shadow-lg text-left flex flex-col justify-between min-h-[90px] ${
                                    !item.moduleId ? 'cursor-default opacity-75' : 'cursor-pointer'
                                  }`}
                                  disabled={!item.moduleId}
                                >
                                  <div>
                                    <ItemIcon size={22} className={`${getIconColorClasses(item.color)} mb-2`} />
                                    <h5 className="font-semibold text-xs leading-tight">{item.name}</h5>
                                  </div>
                                  <p className="text-[10px] opacity-70 mt-1 line-clamp-1">{item.description}</p>
                                </button>
                                {item.moduleId && (
                                  <button
                                    onClick={(e) => handleTogglePin(item, group.id, e)}
                                    className={`absolute top-1.5 right-1.5 p-1 rounded transition-all duration-200 ${
                                      isPinned ? 'bg-blue-500 text-white opacity-100' : 'bg-white/80 text-gray-600 opacity-0 group-hover:opacity-100'
                                    } hover:scale-110 shadow-sm`}
                                    title={isPinned ? 'Fjern fra snarveier' : 'Legg til snarvei'}
                                  >
                                    {isPinned ? <Pin size={12} /> : <PinOff size={12} />}
                                  </button>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* ROW 4: Systemadministrator - kun admin (operatorGroupId === '3') */}
              {dashboardGroups.filter(g => g.id === 'sysadmin').map((group) => {
                const GroupIcon = group.icon;
                return (
                  <div key={group.id} className="bg-white rounded-lg shadow-sm border border-red-200">
                    <div className={`p-4 rounded-t-lg ${getGroupColorClasses(group.color)} border-b-2`}>
                      <div className="flex items-center space-x-3">
                        <GroupIcon size={24} className="text-red-600" />
                        <h3 className="font-bold text-lg">{group.name}</h3>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
                        {group.sections.flatMap((section) =>
                          section.items.map((item) => {
                            const ItemIcon = item.icon;
                            const isPinned = pinnedShortcuts.has(item.id);
                            return (
                              <div key={item.id} className="relative group">
                                <button
                                  onClick={(e) => handleItemClick(item, e)}
                                  className={`${getColorClasses(item.color)} w-full p-3 rounded-lg border-2 transition-all duration-200 transform hover:scale-105 hover:shadow-lg text-left flex flex-col justify-between min-h-[90px] ${
                                    !item.moduleId ? 'cursor-default opacity-75' : 'cursor-pointer'
                                  }`}
                                  disabled={!item.moduleId}
                                >
                                  <div>
                                    <ItemIcon size={22} className={`${getIconColorClasses(item.color)} mb-2`} />
                                    <h5 className="font-semibold text-xs leading-tight">{item.name}</h5>
                                  </div>
                                  <p className="text-[10px] opacity-70 mt-1 line-clamp-1">{item.description}</p>
                                </button>
                                {item.moduleId && (
                                  <button
                                    onClick={(e) => handleTogglePin(item, group.id, e)}
                                    className={`absolute top-1.5 right-1.5 p-1 rounded transition-all duration-200 ${
                                      isPinned ? 'bg-blue-500 text-white opacity-100' : 'bg-white/80 text-gray-600 opacity-0 group-hover:opacity-100'
                                    } hover:scale-110 shadow-sm`}
                                    title={isPinned ? 'Fjern fra snarveier' : 'Legg til snarvei'}
                                  >
                                    {isPinned ? <Pin size={12} /> : <PinOff size={12} />}
                                  </button>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ROW 4: Operator functions - full bredde */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Section header */}
            <div className="p-4 rounded-t-lg bg-gradient-to-r from-purple-100 to-blue-100 border-b-2 border-purple-300">
              <div className="flex items-center space-x-3">
                <Zap size={24} className="text-purple-600" />
                <h3 className="font-bold text-lg text-purple-900">
                  {isOperatorOnly ? 'Mine Oppgaver' : 'Operatør-funksjoner'}
                </h3>
              </div>
            </div>

            {/* Operator modules grid - horisontalt i én rad på store skjermer */}
            <div className="p-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-10 gap-3">
                {operatorModules.filter((item) => isModuleAccessible(item.moduleId || item.id)).map((item) => {
                  const ItemIcon = item.icon;
                  const isPinned = pinnedShortcuts.has(item.id);

                  return (
                    <div key={item.id} className="relative group">
                      <button
                        onClick={(e) => handleItemClick(item, e)}
                        className={`${getColorClasses(item.color)} w-full p-3 rounded-lg border-2 transition-all duration-200 transform hover:scale-105 hover:shadow-lg text-left flex flex-col items-center justify-center min-h-[90px] cursor-pointer`}
                      >
                        <ItemIcon size={28} className={`${getIconColorClasses(item.color)} mb-2`} />
                        <h5 className="font-semibold text-[11px] leading-tight text-center">{item.name}</h5>
                      </button>

                      {/* Pin button */}
                      <button
                        onClick={(e) => handleTogglePin(item, 'operator', e)}
                        className={`absolute top-1 right-1 p-1 rounded transition-all duration-200 ${
                          isPinned ? 'bg-blue-500 text-white opacity-100' : 'bg-white/80 text-gray-600 opacity-0 group-hover:opacity-100'
                        } hover:scale-110 shadow-sm`}
                        title={isPinned ? 'Fjern fra snarveier' : 'Legg til snarvei'}
                      >
                        {isPinned ? <Pin size={12} /> : <PinOff size={12} />}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fast statusbar i bunnen */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-10">
        <div className="bg-gray-100 px-4 py-3">
          <div className="flex items-center justify-between text-sm text-gray-600 w-full">
            <div className="flex items-center space-x-4">
              <span>System: <span className="text-green-600 font-medium">Online</span></span>
              <span>•</span>
              <span>Rolle: {isOperatorOnly ? 'Operatør' : isSystemAdmin ? 'Administrator' : 'Lagersjef'}</span>
            </div>
            <div className="text-xs">
              Publisert: {publishedDate || 'Laster...'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
